import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { Button } from "@circleco/compass/components/Button";
import { TextInput } from "@circleco/compass/components/TextInput";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

import type { CheckoutFormValuesProps } from "../../../types/CheckoutFormValuesProps";

interface CouponFieldProps {
  applyCoupon: (couponCode?: string) => void;
}

export const CouponField = ({ applyCoupon }: CouponFieldProps) => {
  const {
    register,
    watch,
    setError,
    formState: { errors },
  } = useFormContext<CheckoutFormValuesProps>();
  const { couponPreviewMutation } = usePaywallCheckoutContext();

  const couponCode = watch("coupon_code");
  const couponRegistration = register("coupon_code");
  const couponError = errors.coupon_code;

  useEffect(() => {
    const errorBody: any = couponPreviewMutation?.error?.body;

    if (errorBody?.failure?.code?.match(/coupon_/)) {
      setError("coupon_code", {
        type: errorBody?.failure?.code?.replace("coupon_", ""),
        message: errorBody?.failure?.message,
      });
    }
  }, [couponPreviewMutation.error]);

  const validateCoupon = (couponCode: string) =>
    !["", undefined, null].includes(couponCode?.trim());
  const onClick = () => {
    if (validateCoupon(couponCode)) {
      applyCoupon();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && validateCoupon(couponCode)) {
      e.preventDefault();
      applyCoupon();
    }
  };

  return (
    <div className="flex w-full flex-row items-start gap-x-2.5">
      <div className="grow w-[150px] md:w-auto">
        <TextInput
          {...couponRegistration}
          disabled={couponPreviewMutation.isPending}
          invalid={!!couponError}
          error={
            couponError &&
            t(`paywall_checkout.form.errors.coupon_code_${couponError.type}`)
          }
          placeholder={t("coupon_code")}
          aria-label={t("paywall_checkout.form.fields.coupon_code")}
          onKeyDown={onKeyDown}
          data-testid="checkout-form-coupon_code-input"
        />
      </div>
      <output aria-live="polite" className="sr-only">
        {couponCode && t("paywall_checkout.form.buttons.apply_coupon")}
      </output>
      {couponCode && (
        <Button
          type="button"
          size="md"
          className="grow md:grow-0"
          variant="secondary"
          disabled={couponPreviewMutation.isPending}
          loading={couponPreviewMutation.isPending}
          onClick={onClick}
          data-testid="checkout-form-apply-coupon"
        >
          {t("paywall_checkout.form.buttons.apply_coupon")}
        </Button>
      )}
    </div>
  );
};
